'use client';

/**
 * PinnedCard — the voice call's pinned glance-card slot. Desktop (design
 * turn 11 part B) is UNCHANGED by turn 14: it renders the front pin's
 * current/latest content exactly as before — CourseCardFull compact / the
 * media bodies, CardStack (primitives/card-stack.tsx) for >1 item, the
 * "Pinned by Sara" flag header + "Sara pinned this" chip. The owner scoped
 * turn 14 (compact anatomy, real swipe, peekable history stack) to mobile
 * only — every mock is a phone frame — so mobile delegates entirely to
 * MobilePinnedStack (pinned-card-mobile.tsx), which reads the FULL pin
 * history rather than just the front card.
 *
 * The desktop/mobile branch is a JS matchMedia check (useIsDesktop, shared
 * with call-screen.tsx) rather than responsive Tailwind classes — same
 * rationale as CallScreen's own split: the two subtrees carry meaningfully
 * different state (SwipeTrack's drag gesture, the course detail Sheet, the
 * stack's expand/collapse) that must not double-mount behind `hidden sm:`.
 */

import { useChatContext } from '@/contexts/ChatContext';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { PinnedCardShell } from '@/components/assistant/primitives/pinned-card-shell';
import { CardStack } from '@/components/assistant/primitives/card-stack';
import { CourseCardFull } from '@/components/assistant/content/course-card-full';
import { MediaBody } from './pinned-media-body';
import { MobilePinnedStack } from './pinned-card-mobile';

export function PinnedCard() {
  const { pinnedCard, pinHistory, dismissPin, bringPinToFront } =
    useChatContext();
  const isDesktop = useIsDesktop();

  if (!isDesktop) {
    return (
      <MobilePinnedStack
        pinHistory={pinHistory}
        dismissPin={dismissPin}
        bringPinToFront={bringPinToFront}
      />
    );
  }

  if (!pinnedCard) return null;

  return (
    <div className="flex flex-col gap-2 px-6 pb-[16px]">
      <PinnedCardShell onDismiss={dismissPin}>
        {pinnedCard.kind === 'courses' ? (
          pinnedCard.courses.length === 1 ? (
            <CourseCardFull compact course={pinnedCard.courses[0]} />
          ) : (
            <CardStack
              items={pinnedCard.courses}
              renderItem={(course) => (
                <CourseCardFull compact course={course} />
              )}
            />
          )
        ) : pinnedCard.refs.length === 1 ? (
          <MediaBody refItem={pinnedCard.refs[0]} />
        ) : (
          <CardStack
            items={pinnedCard.refs}
            renderItem={(refItem) => <MediaBody refItem={refItem} />}
          />
        )}
      </PinnedCardShell>
      <span className="self-center rounded-[20px] bg-white/60 px-[12px] py-0.5 text-[11px] text-[#b3a996]">
        Sara pinned this · conversation continues
      </span>
    </div>
  );
}

export default PinnedCard;
